import {addEdge, Background, BackgroundVariant, ReactFlow, useEdgesState, useNodesState} from "@xyflow/react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import '@xyflow/react/dist/style.css';
import {LuLoader} from "react-icons/lu";
import {collection, onSnapshot, query, orderBy} from "firebase/firestore";
import {db} from "../../../firebaseConfig"; // Adjust path as needed
import {useOrganization} from "../../../context/OrgContext"; // Adjust path as needed
import DataFlowNode from './DataFlowNode'; // Import the separated component
import NodeDetailsPanel from './NodeDetailsPanel'; // New component we'll create
import GridEdge from './GridEdge';

const proOptions = { hideAttribution: true };

const edgeTypes = {
  grid: GridEdge,
};

const nodeTypes = {
  dataFlow: DataFlowNode,
};

const GenericModuleFlow = ({
  moduleName,
  onModuleComplete = null,
  customNodeTypes = {},
  customBackground = null
}) => {
  const { institution } = useOrganization();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeId, setNodeId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const animationTimeoutRef = useRef(null);
  const unsubscribeRef = useRef(null); // Store the unsubscribe function

  // Merge custom node types with default ones
  const allNodeTypes = { ...nodeTypes, ...customNodeTypes };

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle panel close
  const handlePanelClose = () => {
    setPanelOpen(false);
    setSelectedNode(null);
  };

  // Handle node click
  const handleNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setPanelOpen(true);
  }, []);

  const animateNodesReposition = useCallback(() => {
    if (nodes.length === 0) return;

    const repositionedNodes = repositionNodesByPriority(nodes, panelOpen);
    setNodes(repositionedNodes);
  }, [nodes, panelOpen, screenWidth, screenHeight, setNodes]);

  const calculateNodePositions = useCallback((nodesList, isPanelOpen = false) => {
    const sortedNodes = [...nodesList].sort((a, b) => {
      const priorityA = a.data.priority || 999;
      const priorityB = b.data.priority || 999;
      return priorityA - priorityB;
    });

    return sortedNodes.map((node, index) => {
      const newX = isPanelOpen
        ? (((screenWidth / 2) - 200) / 2) - 75
        : ((screenWidth - 150 * sortedNodes.length) / 2) + index * 150;
      const newY = ((screenHeight - 200 * sortedNodes.length) / 2) + index * 185;

      return {
        ...node,
        position: {
          x: newX,
          y: newY,
        },
        data: {
          ...node.data,
          isPanelOpen: isPanelOpen,
          isFirst: index === 0,
          isLast: index === sortedNodes.length - 1,
          hasIncoming: index > 0,
          hasOutgoing: index < sortedNodes.length - 1
        }
      };
    });
  }, [screenWidth, screenHeight]);

  const repositionNodesByPriority = (nodesList, isPanelOpen = false) => {
    const sortedNodes = [...nodesList].sort((a, b) => {
      const priorityA = a.data.priority || 999;
      const priorityB = b.data.priority || 999;
      return priorityA - priorityB;
    });

    return sortedNodes.map((node, index) => ({
      ...node,
      position: {
        x: (isPanelOpen ? (((screenWidth / 2) - 200) / 2) - 20 : ((screenWidth - 150 * sortedNodes.length) / 2) + index * 150),
        y: ((screenHeight - 200 * sortedNodes.length) / 2) + index * 185,
      },
      data: {
        ...node.data,
        isPanelOpen: isPanelOpen
      }
    }));
  };

  // Reposition nodes when screen size or panel state changes
  useEffect(() => {
    if (nodes.length > 0) {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }

      animationTimeoutRef.current = setTimeout(() => {
        animateNodesReposition();
      }, 50);
    }
  }, [screenWidth, screenHeight, panelOpen, animateNodesReposition]);

  // Function to create edges between nodes based on priority order
  const createPriorityBasedEdges = (nodesList) => {

    const sortedNodes = [...nodesList].sort((a, b) => {
      const priorityA = a.data.priority || 999;
      const priorityB = b.data.priority || 999;
      return priorityA - priorityB;
    });

    const newEdges = [];
    for (let i = 0; i < sortedNodes.length - 1; i++) {
      const currentNode = sortedNodes[i];
      const nextNode = sortedNodes[i + 1];

      const edge = {
        id: `edge-${currentNode.id}-${nextNode.id}`,
        source: currentNode.id,
        target: nextNode.id,
        type: 'grid',
        animated: true,
        markerEnd: {
          type: 'arrowclosed',
          width: 20,
          height: 20,
        },
        style: {
          stroke: '#ffffff50',
          strokeWidth: 1,
        },
      };
      newEdges.push(edge);
    }
    return newEdges;
  };

  // Update edges whenever nodes change
  const updateEdgesForNodes = useCallback((nodesList) => {
    const priorityEdges = createPriorityBasedEdges(nodesList);
    setEdges(priorityEdges);
  }, [setEdges]);

  // Helper function to get icon based on node type
  const getIconForNodeType = (nodeType) => {
    switch (nodeType) {
      case 'database':
        return 'LuDatabase';
      case 'fetcher':
        return 'LuGlobe';
      case 'agent':
        return 'LuBot';
      case 'recipients':
        return 'LuWebhook';
      case 'worker':
        return 'LuCpu';
      case 'server':
        return 'LuServer';
      default:
        return 'LuBot';
    }
  };

  // Transform Firestore document data to node format
  const transformDocToNode = useCallback((doc, index, totalNodes) => {
    const nodeData = doc.data();

    return {
      id: doc.id,
      type: 'dataFlow',
      position: {
        x: (panelOpen ? (((screenWidth / 2) - 200) / 2) - 90 : ((screenWidth - 150 * totalNodes) / 2) + index * 150),
        y: ((screenHeight - 200 * totalNodes) / 2) + index * 185,
      },
      data: {
        label: nodeData.title || 'Untitled Node',
        type: nodeData.type || 'unknown',
        icon: getIconForNodeType(nodeData.type),
        status: nodeData.status || 'not_configured',
        description: nodeData.description || '',
        config: nodeData.config || {},
        priority: nodeData.priority || 999,
        scrapeURL: nodeData.scrapeURL || '',
        storageUsed: nodeData.storageUsed,
        documentCount: nodeData.documentCount,
        estimatedSize: nodeData.estimatedSize,
        dbType: nodeData.dbType,
        handle: nodeData.handle,
        workerStatus: nodeData.workerStatus,
        visibility: nodeData.visibility,
        inputValue: nodeData.inputValue,
        isFirst: index === 0,
        isLast: index === totalNodes - 1,
        hasIncoming: index > 0,
        hasOutgoing: index < totalNodes - 1,
        isPanelOpen: panelOpen
      }
    };
  }, [screenWidth, screenHeight, panelOpen]);

  // Set up real-time listener for Firestore
  useEffect(() => {
    if (!institution?.id || !moduleName) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Clean up existing listener
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    try {
      const nodesCollectionRef = collection(db, `colleges/${institution.id}/configs/${moduleName}/nodes`);
      // Optional: Add ordering by priority
      const q = query(nodesCollectionRef, orderBy('priority', 'asc'));

      // Set up real-time listener
      const unsubscribe = onSnapshot(q, (snapshot) => {

        const fetchedNodes = [];

        snapshot.forEach((doc) => {
          const transformedNode = transformDocToNode(doc, fetchedNodes.length, snapshot.size);
          fetchedNodes.push(transformedNode);
        });

        // Sort nodes by priority
        const sortedNodes = fetchedNodes.sort((a, b) => {
          const priorityA = a.data.priority || 999;
          const priorityB = b.data.priority || 999;
          return priorityA - priorityB;
        });

        // Update positions after sorting
        const repositionedNodes = sortedNodes.map((node, index) => ({
          ...node,
          position: {
            x: (panelOpen ? (((screenWidth / 2) - 200) / 2) - 90 : ((screenWidth - 150 * sortedNodes.length) / 2) + index * 150),
            y: ((screenHeight - 200 * sortedNodes.length) / 2) + index * 185,
          },
          data: {
            ...node.data,
            isFirst: index === 0,
            isLast: index === sortedNodes.length - 1,
            hasIncoming: index > 0,
            hasOutgoing: index < sortedNodes.length - 1
          }
        }));

        setNodes(repositionedNodes);

        // Update node ID counter
        if (repositionedNodes.length > 0) {
          const maxId = Math.max(...repositionedNodes.map(node => {
            const idNum = parseInt(node.id.replace('node-', '').replace(/\D/g, ''));
            return isNaN(idNum) ? 0 : idNum;
          }));
          setNodeId(maxId + 1);
        }

        setLoading(false);
      }, (error) => {
        setError(`Failed to listen to ${moduleName} data: ${error.message}`);
        setLoading(false);
      });

      // Store the unsubscribe function
      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      setError(`Failed to set up ${moduleName} listener: ${err.message}`);
      setLoading(false);
    }

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [moduleName, institution?.id, transformDocToNode, setNodes]);

  // Update edges when nodes change
  useEffect(() => {
    if (nodes.length > 0) {
      updateEdgesForNodes(nodes);
    }
  }, [nodes, updateEdgesForNodes]);

  // Update selected node data when nodes change (for real-time panel updates)
  useEffect(() => {
    if (selectedNode && panelOpen) {
      const updatedNode = nodes.find(node => node.id === selectedNode.id);
      if (updatedNode && JSON.stringify(updatedNode.data) !== JSON.stringify(selectedNode.data)) {
        setSelectedNode(updatedNode);
      }
    }
  }, [nodes, selectedNode, panelOpen]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
  }, [onNodesChange]);

  // Cleanup animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return (
    <div className="h-full w-full border-t border-[#ffffff15] relative">
      <style jsx>{`
        .react-flow__node {
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        
        .react-flow__edge {
          transition: all 0.4s ease-in-out !important;
        }
        
        .react-flow__edge path {
          transition: stroke-dashoffset 2s ease-in-out !important;
        }

        .react-flow__edge-path {
          stroke: #ffffff80;
          stroke-width: 3;
        }
        
        .react-flow__arrowhead {
          fill: #ffffff80;
          stroke: #ffffff80;
        }

        .react-flow__handle {
          background: transparent !important;
          border: none !important;
          width: 12px !important;
          height: 12px !important;
          opacity: 0 !important;
        }
      `}</style>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        nodeTypes={allNodeTypes}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        attributionPosition="bottom-left"
        proOptions={proOptions}
        fitViewOptions={{
          duration: 800,
          padding: 0.2
        }}
      >
        {customBackground || (
          <Background
            variant={BackgroundVariant.Dots}
            gap={25}
            size={1.5}
            color="#ffffff35"
            style={{ backgroundColor: 'transparent' }}
          />
        )}
      </ReactFlow>

      {/* Node Details Panel */}
      {panelOpen && (
        <NodeDetailsPanel
          node={selectedNode}
          onClose={handlePanelClose}
          moduleName={moduleName}
          institution={institution}
        />
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="flex items-center gap-3 text-white/80 bg-[#ffffff10] px-6 py-4 rounded-lg border border-[#ffffff15]">
            <LuLoader className="w-5 h-5 animate-spin" />
            <span>Loading {moduleName} data...</span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg max-w-md">
            <p className="text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-3 px-4 py-2 bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericModuleFlow;